"use client";

import { c as _c } from "react/compiler-runtime";
import Address from "@/components/address/Address";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import RememeImage from "@/components/nft-image/RememeImage";
import NFTMarketplaceLinks from "@/components/nft-marketplace-links/NFTMarketplaceLinks";
import { RememeReferencesGrid } from "@/components/rememes/RememeReferences";
import { AdditionalDetailsSection, MetadataCard } from "@/components/the-memes/MemePageAdditionalDetails";
import { publicEnv } from "@/config/env";
import { MEMES_CONTRACT, OPENSEA_STORE_FRONT_CONTRACT } from "@/constants/constants";
import { useTitle } from "@/contexts/TitleContext";
import type { DBResponse } from "@/entities/IDBResponse";
import type { IAttribute, NFT, Rememe } from "@/entities/INFT";
import { areEqualAddresses, formatAddress, isIPFS, isUrl, numberWithCommas, parseIpfsUrl, parseNftDescriptionToHtml } from "@/helpers/Helpers";
import useCapacitor from "@/hooks/useCapacitor";
import { useIdentity } from "@/hooks/useIdentity";
import { fetchAllPages, fetchUrl } from "@/services/6529api";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { ArrowTopRightOnSquareIcon, CodeBracketSquareIcon, GlobeAltIcon, LinkIcon, SwatchIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useEnsName } from "wagmi";
export { printMemeReferences } from "@/components/rememes/RememeReferences";
interface Props {
  contract: string;
  id: string;
}
enum Tabs {
  LIVE = "Overview",
  METADATA = "Metadata",
  REFERENCES = "References",
}
function getRememeTitle(rememe: Rememe) {
  const metadata = getRememeMetadataRecord(rememe);
  const metadataName = metadata["name"];
  if (typeof metadataName === "string" && metadataName.length > 0) {
    return metadataName;
  }
  return `${formatAddress(rememe.contract)} #${rememe.id}`;
}
function getRememeCollectionName(rememe: Rememe) {
  const collectionName = rememe.contract_opensea_data.collectionName;
  return typeof collectionName === "string" && collectionName.length > 0 ? collectionName : formatAddress(rememe.contract);
}
function getRememeMetadataRecord(rememe: Rememe): Record<string, unknown> {
  if (rememe.metadata !== null && rememe.metadata !== undefined && typeof rememe.metadata === "object" && !Array.isArray(rememe.metadata)) {
    return rememe.metadata as Record<string, unknown>;
  }
  return {};
}
function getRememeTokenLabel(rememe: Rememe) {
  if (areEqualAddresses(rememe.contract, OPENSEA_STORE_FRONT_CONTRACT)) {
    return rememe.contract_opensea_data.collectionName;
  }
  return `${getRememeCollectionName(rememe)} #${rememe.id}`;
}
function RememeInfoMetric(t0) {
  const $ = _c(8);
  const {
    label,
    children,
    valueClassName: t1
  } = t0;
  const valueClassName = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <div className="tw-mb-1 tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-400 md:tw-mb-2">{label}</div>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1.5 tw-break-words tw-text-sm tw-font-semibold tw-leading-5 tw-text-white md:tw-text-lg md:tw-leading-6 ${valueClassName}`;
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className="tw-min-w-[8.5rem]">{t2}{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function RememeAddressValue(t0) {
  const $ = _c(16);
  const {
    wallet,
    display
  } = t0;
  const identity = display ?? wallet;
  let t1;
  if ($[0] !== identity) {
    t1 = {
      handleOrWallet: identity,
      initialProfile: null
    };
    $[0] = identity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    profile
  } = useIdentity(t1);
  const t2 = profile?.pfp;
  const t3 = `${identity} avatar`;
  let t4;
  if ($[2] !== identity) {
    t4 = <RememeAvatarFallback value={identity} />;
    $[2] = identity;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = <ProfileAvatar pfpUrl={t2} size={ProfileBadgeSize.SMALL} alt={t3} fallbackContent={t4} />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = wallet as `0x${string}`;
  let t7;
  if ($[8] !== t6) {
    t7 = [t6];
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== display || $[11] !== t7) {
    t8 = <span className="tw-min-w-0 tw-whitespace-nowrap [&_*]:tw-whitespace-nowrap"><Address wallets={t7} display={display} /></span>;
    $[10] = display;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t5 || $[14] !== t8) {
    t9 = <>{t5}{t8}</>;
    $[13] = t5;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function RememeAvatarFallback(t0) {
  const $ = _c(4);
  const {
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = getInitials(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-text-iron-400">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function getInitials(value: string) {
  return value.split(/[,\s]+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase();
}
function RememeExternalLink(t0) {
  const $ = _c(8);
  const {
    href,
    children,
    align: t1,
    openInNewTab: t2
  } = t0;
  const align = t1 === undefined ? "center" : t1;
  const openInNewTab = t2 === undefined ? true : t2;
  const alignmentClass = align === "start" ? "tw-items-start" : "tw-items-center";
  let t3;
  if ($[0] !== openInNewTab) {
    t3 = openInNewTab ? {
      target: "_blank",
      rel: "noopener noreferrer"
    } : {};
    $[0] = openInNewTab;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `tw-inline-flex tw-min-w-0 ${alignmentClass} tw-gap-3 tw-break-all tw-text-sm tw-font-semibold tw-leading-5 tw-text-white tw-no-underline hover:tw-text-iron-300 md:tw-text-lg md:tw-leading-6`;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowTopRightOnSquareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-400" />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== children || $[4] !== href || $[5] !== t3 || $[6] !== t4) {
    t6 = <a href={href} {...t3} className={t4}>{children}{t5}</a>;
    $[3] = children;
    $[4] = href;
    $[5] = t3;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function RememeMetadataLink(t0) {
  const $ = _c(4);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArrowTopRightOnSquareIcon className="tw-mt-0.5 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-500" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== href) {
    t2 = <a href={href} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-min-w-0 tw-items-start tw-gap-2 tw-break-all tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-300 tw-no-underline hover:tw-text-iron-100">{children}{t1}</a>;
    $[1] = children;
    $[2] = href;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function RememeTabButton(t0) {
  const $ = _c(4);
  const {
    title,
    isActive,
    onClick
  } = t0;
  const t1 = `tw-m-0 tw-flex tw-items-center tw-whitespace-nowrap tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-bg-transparent tw-px-1 tw-py-4 tw-text-base tw-font-semibold tw-leading-4 tw-no-underline tw-transition tw-duration-300 tw-ease-out ${isActive ? "tw-pointer-events-none tw-border-primary-400 tw-text-iron-100" : "tw-cursor-pointer tw-border-transparent tw-text-iron-500 hover:tw-border-gray-300 hover:tw-text-iron-100"}`;
  let t2;
  if ($[0] !== onClick || $[1] !== t1 || $[2] !== title) {
    t2 = <button type="button" className={t1} onClick={onClick}>{title}</button>;
    $[0] = onClick;
    $[1] = t1;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export default function RememePage(props: Readonly<Props>) {
  const {
    setTitle
  } = useTitle();
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const [rememe, setRememe] = useState<Rememe>();
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.LIVE);
  const [memes, setMemes] = useState<NFT[]>([]);
  useEffect(() => {
    async function fetchRememeAndReferences() {
      if (!props.contract || !props.id) {
        return;
      }
      try {
        const response = await fetchUrl<DBResponse<Rememe>>(`${publicEnv.API_ENDPOINT}/api/rememes?contract=${props.contract}&id=${props.id}`);
        const fetchedRememe = response.data[0];
        if (response.data.length !== 1 || fetchedRememe === undefined) {
          return;
        }
        setRememe(fetchedRememe);
        const metadataName = getRememeMetadataRecord(fetchedRememe)["name"];
        if (typeof metadataName === "string" && metadataName.length > 0) {
          setTitle(`${metadataName} | ReMemes | 6529.io`);
        }
        const responseNfts = await fetchAllPages<NFT>(`${publicEnv.API_ENDPOINT}/api/nfts?contract=${MEMES_CONTRACT}&id=${fetchedRememe.meme_references.join(",")}`);
        setMemes(responseNfts.sort((a, b) => a.id - b.id));
      } catch (error: unknown) {
        console.error("Failed to fetch ReMeme", error);
      }
    }
    void fetchRememeAndReferences();
  }, [props.contract, props.id, setTitle]);
  const ensResolutionDeployer = useEnsName({
    address: rememe ? rememe.deployer as `0x${string}` : undefined,
    query: {
      enabled: rememe !== undefined
    },
    chainId: 1
  });
  const ensResolutionAddedBy = useEnsName({
    address: rememe ? rememe.added_by as `0x${string}` : undefined,
    query: {
      enabled: rememe !== undefined
    },
    chainId: 1
  });
  function printContent() {
    switch (activeTab) {
      case Tabs.LIVE:
        return printOverview();
      case Tabs.METADATA:
        return printMetadata();
      case Tabs.REFERENCES:
        return <RememeReferencesGrid memes={memes} />;
    }
  }
  function printStaticCardHeader() {
    if (!rememe) {
      return null;
    }
    const isOpenSeaStorefront = areEqualAddresses(rememe.contract, OPENSEA_STORE_FRONT_CONTRACT);
    const addedByVisible = typeof rememe.added_by === "string" && rememe.added_by.length > 0 && !areEqualAddresses(rememe.deployer, rememe.added_by);
    return <div className="tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-x-10 lg:tw-grid-cols-[minmax(0,11fr)_minmax(0,9fr)] xl:tw-gap-x-16">
        <div className="tw-relative lg:tw-flex lg:tw-flex-col lg:tw-self-stretch">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-bg-iron-950 lg:tw-flex-1">
            <RememeImage nft={rememe} animation={true} height={650} />
          </div>
        </div>
        <div className="tw-pt-6 md:tw-pt-8 lg:tw-pt-2">
          <section aria-label="ReMeme details" className="tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-pb-6 md:tw-pb-8">
            <div className="tw-space-y-5">
              <RememeInfoMetric label="Collection">
                {getRememeTokenLabel(rememe)}
              </RememeInfoMetric>
              {!isOpenSeaStorefront && <RememeInfoMetric label="Created by" valueClassName="!tw-flex-nowrap [&_a]:tw-text-white [&_a:hover]:tw-text-iron-300">
                  <RememeAddressValue wallet={rememe.deployer} display={ensResolutionDeployer.data ?? undefined} />
                </RememeInfoMetric>}
              {addedByVisible && <RememeInfoMetric label="Added by" valueClassName="!tw-flex-nowrap [&_a]:tw-text-white [&_a:hover]:tw-text-iron-300">
                  <RememeAddressValue wallet={rememe.added_by} display={ensResolutionAddedBy.data ?? undefined} />
                </RememeInfoMetric>}
            </div>
          </section>
          <section className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-py-6 md:tw-py-8">
            <div className="tw-space-y-8">
              <div className="tw-space-y-4">
                <div className="tw-flex tw-min-w-0">
                  <RememeExternalLink href={`https://etherscan.io/token/${rememe.contract}/?a=${rememe.id}`}>
                    <Image unoptimized width={0} height={0} style={{
                    width: "20px",
                    height: "auto"
                  }} src="/etherscan_w.png" alt="etherscan" />
                    <span>{getRememeCollectionName(rememe)}</span>
                  </RememeExternalLink>
                </div>
                {rememe.contract_opensea_data.externalUrl && <div className="-tw-ml-0.5 tw-flex tw-min-w-0">
                    <RememeExternalLink href={rememe.contract_opensea_data.externalUrl} openInNewTab={false}>
                      <GlobeAltIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0 tw-text-iron-400" />
                      <span>{rememe.contract_opensea_data.externalUrl}</span>
                    </RememeExternalLink>
                  </div>}
                {rememe.contract_opensea_data.twitterUsername && <div className="tw-flex tw-min-w-0">
                    <RememeExternalLink href={`https://x.com/${rememe.contract_opensea_data.twitterUsername}`}>
                      <Image unoptimized width={0} height={0} style={{
                    width: "20px",
                    height: "auto"
                  }} src="/twitter.png" alt={`${rememe.contract_opensea_data.twitterUsername} Twitter`} />
                      <span>
                        @{rememe.contract_opensea_data.twitterUsername}
                      </span>
                    </RememeExternalLink>
                  </div>}
              </div>
              {(!capacitor.isIos || country === "US") && <div className="tw-w-fit tw-max-w-full">
                  <NFTMarketplaceLinks contract={rememe.contract} id={rememe.id} />
                </div>}
            </div>
          </section>
        </div>
      </div>;
  }
  function printOverview() {
    if (!rememe) {
      return null;
    }
    const metadata = getRememeMetadataRecord(rememe);
    const description = typeof metadata["description"] === "string" ? metadata["description"] : "";
    return <div className="tw-space-y-4 tw-pb-8">
        <section className="tw-max-w-4xl tw-text-pretty tw-pb-3">
          <div className="tw-text-base tw-font-normal tw-text-iron-300" dangerouslySetInnerHTML={{
          __html: parseNftDescriptionToHtml(description)
        }} />
        </section>
        {printReplicas()}
      </div>;
  }
  function printReplicas() {
    if (!rememe || rememe.replicas.length <= 1) {
      return null;
    }
    return <section className="tw-pb-5 tw-pt-3">
        <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-1">
          <h2 className="tw-mb-0 tw-text-lg tw-font-semibold tw-leading-6 tw-text-iron-100">
            Replicas
          </h2>
          <span className="tw-text-sm tw-font-semibold tw-leading-5 tw-text-iron-400">
            (x{numberWithCommas(rememe.replicas.length)})
          </span>
        </div>
        <div className="tw-mt-1 tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-500">
          * Replicas are tokens with identical images
        </div>
        <div className="tw-mt-5 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          {rememe.replicas.filter(rep => Number(rep) !== Number.parseInt(rememe.id, 10)).map(rep_0 => <Link href={`/rememes/${rememe.contract}/${rep_0}`} className="tw-inline-flex tw-items-center tw-rounded-md tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-leading-5 tw-text-iron-200 tw-no-underline tw-transition-colors hover:tw-border-iron-500 hover:tw-bg-iron-900 hover:tw-text-white" key={`${rememe.contract}-${rep_0}-replica`}>
                #{rep_0}
              </Link>)}
        </div>
      </section>;
  }
  function getAttributes(): IAttribute[] {
    if (!rememe) {
      return [];
    }
    const metadata_0 = getRememeMetadataRecord(rememe);
    const attributes = metadata_0["attributes"];
    if (Array.isArray(attributes)) {
      return attributes as IAttribute[];
    }
    if (attributes !== null && attributes !== undefined && typeof attributes === "object") {
      return Object.entries(attributes as Record<string, string | number>).map(([key, value]) => ({
        trait_type: key,
        value
      }));
    }
    return [];
  }
  function printValue(value_0: string) {
    if (isUrl(value_0) || isIPFS(value_0)) {
      return <RememeMetadataLink href={parseIpfsUrl(value_0)}>
          <span>{value_0}</span>
        </RememeMetadataLink>;
    }
    return value_0;
  }
  function getMetadataRows() {
    if (!rememe) {
      return [];
    }
    return Object.entries(getRememeMetadataRecord(rememe)).flatMap(([key_0, value_1]) => {
      if (key_0 === "name" || key_0 === "description" || key_0 === "attributes") {
        return [];
      }
      if (typeof value_1 === "string") {
        return [{
          key: key_0,
          label: key_0,
          value: printValue(value_1)
        }];
      }
      if (value_1 !== null && value_1 !== undefined && typeof value_1 === "object" && !Array.isArray(value_1)) {
        return Object.entries(value_1 as Record<string, unknown>).flatMap(([nestedKey, nestedValue]) => {
          if (typeof nestedValue !== "string") {
            return [];
          }
          return [{
            key: `${key_0}-${nestedKey}`,
            label: `${key_0}::${nestedKey}`,
            value: nestedValue
          }];
        });
      }
      return [];
    });
  }
  function printMetadata() {
    if (!rememe) {
      return null;
    }
    const attributes_0 = getAttributes();
    const metadataRows = getMetadataRows();
    return <div className="tw-space-y-14 tw-pb-8">
        <section>
          <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-6 sm:tw-gap-x-8 md:tw-grid-cols-3 md:tw-gap-x-10">
            <div className="tw-min-w-0 md:tw-col-span-2">
              <RememeInfoMetric label="Token URI">
                <RememeExternalLink href={rememe.token_uri} align="start">
                  <LinkIcon className="tw-mt-0.5 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-500 md:tw-mt-1" />
                  <span className="tw-min-w-0 tw-break-words">
                    {rememe.token_uri}
                  </span>
                </RememeExternalLink>
              </RememeInfoMetric>
            </div>
            <RememeInfoMetric label="Token Type">
              {rememe.token_type}
            </RememeInfoMetric>
          </div>
        </section>

        {metadataRows.length > 0 && <AdditionalDetailsSection title="Metadata" icon={CodeBracketSquareIcon}>
            <div className="tw-overflow-x-auto tw-overflow-y-hidden tw-pb-2 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">
              <table className="tw-w-full tw-min-w-[640px] tw-border-collapse">
                <tbody>
                  {metadataRows.map(row => <tr key={row.key} className="odd:tw-bg-iron-900/40 even:tw-bg-transparent">
                      <td className="tw-w-52 tw-whitespace-nowrap tw-border-0 tw-px-4 tw-py-3 tw-align-top tw-text-xs tw-font-semibold tw-uppercase tw-leading-5 tw-text-iron-500">
                        {row.label}
                      </td>
                      <td className="tw-border-0 tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-200">
                        {row.value}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </AdditionalDetailsSection>}

        {attributes_0.length > 0 && <AdditionalDetailsSection title="Properties" icon={SwatchIcon}>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 lg:tw-grid-cols-4">
              {attributes_0.map(attribute => <MetadataCard key={`${attribute.trait_type}-${attribute.value}`} label={attribute.trait_type} value={attribute.value} />)}
            </div>
          </AdditionalDetailsSection>}
      </div>;
  }
  function printTabs() {
    if (!rememe) {
      return null;
    }
    return <nav aria-label="ReMemes page sections" className="tw-relative tw-mb-8 tw-overflow-hidden tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800">
        <div className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden">
          <div className="-tw-mb-px tw-flex tw-min-w-max tw-gap-x-3 lg:tw-gap-x-4">
            {Object.values(Tabs).map(tab => <RememeTabButton key={`${tab}-tab`} title={tab} isActive={activeTab === tab} onClick={() => setActiveTab(tab)} />)}
          </div>
        </div>
      </nav>;
  }
  return <div className="tailwind-scope tw-min-h-[calc(100vh-100px)] tw-border tw-border-y-0 tw-border-l-0 tw-border-solid tw-border-iron-800 tw-bg-[#0D0D0F] tw-pb-5 tw-text-white">
      <div className="tw-px-4 tw-py-4 md:tw-px-6 md:tw-pb-10 lg:tw-px-8">
        <header className="tw-pb-8">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-mb-0 tw-flex tw-items-center">
              <Link href="/rememes" className="tw-group -tw-ml-2 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-py-2 tw-text-xs tw-font-semibold tw-leading-5 tw-text-iron-300 tw-no-underline tw-transition-colors hover:tw-text-iron-400 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400">
                <ArrowLeftIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-transition-transform group-hover:-tw-translate-x-0.5" />
                ReMemes
              </Link>
            </div>
            {rememe ? <h1 className="tw-mb-0 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 md:tw-flex-nowrap md:tw-gap-x-0" aria-label={`ReMemes - ${getRememeTitle(rememe)}`}>
                <span className="tw-shrink-0">
                  <Image unoptimized loading="eager" width={0} height={0} style={{
                width: "200px",
                height: "auto"
              }} src="/re-memes.png" alt="re-memes" />
                </span>
                <span aria-hidden="true" className="tw-mx-3 tw-h-5 tw-w-px tw-self-center tw-bg-white/[0.16] sm:tw-h-6" />
                <span className="tw-mb-0 tw-min-w-0 tw-whitespace-normal tw-break-words tw-text-lg tw-font-semibold tw-leading-tight tw-text-iron-100 sm:tw-text-2xl">
                  {getRememeTitle(rememe)}
                </span>
              </h1> : <Image unoptimized loading="eager" width={0} height={0} style={{
            width: "200px",
            height: "auto"
          }} src="/re-memes.png" alt="re-memes" />}
          </div>
        </header>
        {rememe && <>
            {printStaticCardHeader()}
            {printTabs()}
            {printContent()}
          </>}
      </div>
    </div>;
}